/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Hidden } from "react-awesome-styled-grid";
import { If, Then, Else } from "react-if";
import { Text, theme, PieChart, Box, SVGIcon, Tooltip } from "@nordcloud/gnui";

enum COLORS {
  Success = "success",
  Warning = "warning",
  Danger = "danger",
  Notification = "notification",
}

type Status = {
  color: COLORS;
};

const getStatus = (progress: number, isNotification: boolean): Status => {
  if (isNotification) {
    return {
      color: COLORS.Notification,
    };
  }

  if (progress > 100) {
    return {
      color: COLORS.Danger,
    };
  }

  if (progress > 0) {
    return {
      color: COLORS.Success,
    };
  }

  return {
    color: COLORS.Warning,
  };
};

type Props = {
  progress: number;
  showBudget: boolean;
  isForecast: boolean;
};

export function CostChart({ progress, showBudget, isForecast }: Props) {
  const status = getStatus(progress, !showBudget);

  return (
    <Hidden xs>
      <div
        css={{
          marginRight: theme.spacing.spacing05,
          height: "100%",
          display: "flex",
        }}
      >
        <Box boxStyle="lightGrey" minHeight="100%">
          <PieChart
            progress={progress}
            size={144}
            strokeWidth={20}
            color={status.color}
          >
            <If condition={showBudget}>
              <Then>
                <Text color={status.color} size="lg" tag="div" weight="medium">
                  {progress}%
                </Text>
                <Text
                  color={theme.colors.darks[3]}
                  size="xs"
                  mb="0"
                  align="center"
                >
                  <If condition={isForecast}>
                    <Then>
                      forecasted
                      <br /> budget usage
                    </Then>
                    <Else>budget used</Else>
                  </If>
                </Text>
              </Then>
              <Else>
                <NoBudgetTooltip />
              </Else>
            </If>
          </PieChart>
        </Box>
      </div>
    </Hidden>
  );
}

function NoBudgetTooltip() {
  return (
    <Tooltip
      caption="To compare forecast vs budget, set all environment budgets to &gt; 0"
      status="notification"
      minWidth="12rem"
    >
      <SVGIcon
        name="help"
        color="notification"
        css={{ marginTop: theme.spacing.spacing02 }}
      />
    </Tooltip>
  );
}
